// src/components/ProductModal.jsx
import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { CartContext } from '../CartContext';
import { triggerHaptic } from '../utils/haptics';
import { useText } from '../utils/locales';

const CoffeeBeanIcon = ({ active }) => (
  <svg 
    viewBox="0 0 64 64" 
    className={`w-3.5 h-3.5 transition-all duration-500 ${active ? 'text-[#6F4E37]' : 'text-tg-hint opacity-20 grayscale'}`}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M48.8,12.2c-10.4-10.4-27.2-10.4-37.6,0c-10.4,10.4-10.4,27.2,0,37.6c10.4,10.4,27.2,10.4,37.6,0 C59.2,39.4,59.2,22.6,48.8,12.2z M17.2,40.8c-2-2-2.9-4.7-2.6-7.3c0.3-2.6,1.6-4.9,3.6-6.6c4-3.4,9.6-3.8,14-1.2l-15,15.1 C17.2,40.8,17.2,40.8,17.2,40.8z M44.6,25.4l-14.1,14c-1.8,1.8-4.2,2.7-6.6,2.6c-2.4-0.1-4.7-1.1-6.4-2.8c-0.1-0.1-0.1-0.1-0.2-0.2 l15-15c2.1-2.1,5.1-3.2,8.1-2.9C43.3,21.5,45.4,23.1,44.6,25.4z" />
  </svg>
);

const ProductModal = ({ product, isOpen, onClose }) => {
  const { addToCart } = useContext(CartContext);
  const t = useText();
  
  const [quantity, setQuantity] = useState(1);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);

  // --- Парсинг характеристик (Крепость, Обжарка и т.д.) ---
  const tagsString = product?.tags ? String(product.tags) : '';
  const rawTags = tagsString.split(',').map(tag => tag.trim()).filter(Boolean);
  
  const statsKeys = [
    { key: 'крепость', label: 'Крепость' },
    { key: 'обжарка', label: 'Обжарка' },
    { key: 'насыщенность', label: 'Насыщенность' },
    { key: 'кислотность', label: 'Кислотность' }
  ];

  const itemStats = [];
  const normalTags = [];

  rawTags.forEach(tag => {
    let isStat = false;
    for (const stat of statsKeys) {
      const regex = new RegExp(`${stat.key}[:\\s]*([1-5])`, 'i');
      const match = tag.match(regex);
      if (match) {
        itemStats.push({ label: stat.label, value: parseInt(match[1], 10) });
        isStat = true;
        break;
      }
    }
    if (!isStat) normalTags.push(tag);
  });

  // --- 1. Инициализация при открытии ---
  useEffect(() => {
    if (product) {
      setQuantity(1);
      
      const initialMods = [];
      if (product.modifiers && product.modifiers.length > 0) {
        product.modifiers.forEach((group, idx) => {
          const gName = group.groupName || group.name || `Опции ${idx + 1}`;
          const uniqueGroupId = `${gName}_${idx}`;
          
          if (group.min >= 1 && group.options.length > 0) {
            initialMods.push({
              ...group.options[0],
              groupId: uniqueGroupId,
              originalGroupName: gName
            });
          }
        });
      }
      setSelectedModifiers(initialMods);
    }
  }, [product]);

  // --- 2. Пересчет итоговой цены ---
  useEffect(() => {
    if (!product) return;
    let price = product.price;
    selectedModifiers.forEach(mod => { price += mod.price; });
    setTotalPrice(price * quantity);
  }, [product, selectedModifiers, quantity]);

  // --- 3. Обработчик клика по модификаторам ---
  const handleModifierToggle = (group, option, groupIdx) => {
    triggerHaptic('selection');
    const gName = group.groupName || group.name || `Опции ${groupIdx + 1}`;
    const uniqueGroupId = `${gName}_${groupIdx}`;
    const isMultiSelect = group.max > 1;
    
    setSelectedModifiers(prev => {
      if (!isMultiSelect) {
        const filtered = prev.filter(m => m.groupId !== uniqueGroupId);
        return [...filtered, { ...option, groupId: uniqueGroupId, originalGroupName: gName }];
      } else {
        const exists = prev.find(m => m.name === option.name && m.groupId === uniqueGroupId);
        if (exists) {
          return prev.filter(m => m !== exists);
        } else {
          const currentCount = prev.filter(m => m.groupId === uniqueGroupId).length;
          if (currentCount < group.max) {
            return [...prev, { ...option, groupId: uniqueGroupId, originalGroupName: gName }];
          }
          return prev;
        }
      }
    });
  };

  const handleAddToCart = () => {
    triggerHaptic('success');
    if (product.modifiers) {
      for (let i = 0; i < product.modifiers.length; i++) {
        const group = product.modifiers[i];
        const gName = group.groupName || group.name || `Опции ${i + 1}`;
        const uniqueGroupId = `${gName}_${i}`;
        const selectedCount = selectedModifiers.filter(m => m.groupId === uniqueGroupId).length;
        if (selectedCount < group.min) {
          alert(`Пожалуйста, выберите: ${gName}`);
          return;
        }
      }
    }
    addToCart(product, selectedModifiers, quantity);
    onClose();
  };

  if (!isOpen || !product) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 bg-tg-bg z-50 rounded-t-[32px] overflow-hidden max-h-[90vh] flex flex-col shadow-2xl"
          >
            <div className="relative h-64 w-full shrink-0 bg-gray-100">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              <button onClick={onClose} className="absolute top-4 right-4 bg-black/30 p-2 rounded-full backdrop-blur-md text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 pb-32 custom-scrollbar">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-tg-text leading-tight mb-2">{product.name}</h2>
                <p className="text-tg-hint text-sm leading-relaxed font-medium">{product.description}</p>
                
                {/* НОВЫЙ БЛОК: Характеристики (Зерна) и Теги */}
                <div className="flex flex-col gap-4 mt-4">
                  {itemStats.length > 0 && (
                    <div className="bg-tg-secondary-bg p-4 rounded-2xl flex flex-col gap-3 shadow-sm">
                      {itemStats.map((stat, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-[10px] uppercase font-black text-tg-hint tracking-widest">{stat.label}</span>
                          <div className="flex gap-1">
                            {[1,2,3,4,5].map(b => <CoffeeBeanIcon key={b} active={b <= stat.value} />)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {normalTags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {normalTags.map((tag, idx) => (
                        <span key={idx} className="bg-tg-button/10 text-tg-button text-[10px] font-black px-3 py-1.5 rounded-xl uppercase tracking-wider">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ВОССТАНОВЛЕННЫЙ БЛОК: МОДИФИКАТОРЫ */}
              {product.modifiers && product.modifiers.map((group, groupIdx) => {
                const groupName = group.groupName || group.name || `Опции`;
                const groupNameLower = groupName.toLowerCase();
                const isMultiSelect = group.max > 1;
                const isBaseVariant = groupNameLower.includes('вес') || groupNameLower.includes('размер') || groupNameLower.includes('объем');

                return (
                  <div key={groupIdx} className="mb-6 last:mb-0">
                    <h3 className="text-tg-text font-bold text-sm uppercase tracking-wide mb-3 flex justify-between">
                        {groupName}
                        {isMultiSelect && <span className="text-[10px] font-normal text-tg-hint normal-case bg-tg-secondary-bg px-2 rounded-full">можно несколько</span>}
                    </h3>
                    
                    <div className="space-y-2">
                      {group.options.map((option, optIdx) => {
                        const gName = group.groupName || group.name || `Опции ${groupIdx + 1}`;
                        const uniqueGroupId = `${gName}_${groupIdx}`;
                        const isSelected = selectedModifiers.some(m => m.name === option.name && m.groupId === uniqueGroupId);

                        const renderPrice = () => {
                            if (option.price_label) return <span className="text-sm font-bold text-tg-button">{option.price_label}</span>;
                            if (isBaseVariant) {
                                const fullPrice = product.price + option.price;
                                return <span className={`text-sm font-bold whitespace-nowrap ${isSelected ? 'text-tg-button' : 'text-tg-text'}`}>{fullPrice.toFixed(0)} {t.currency}</span>;
                            }
                            if (option.price > 0) {
                                return <span className={`text-sm font-bold whitespace-nowrap ${isSelected ? 'text-tg-button' : 'text-tg-text'}`}>+{option.price.toFixed(0)} {t.currency}</span>;
                            }
                            return null;
                        };

                        return (
                          <div 
                            key={optIdx} 
                            onClick={() => handleModifierToggle(group, option, groupIdx)} 
                            className={`flex justify-between items-center p-4 rounded-xl border cursor-pointer transition-all active:scale-[0.98] ${isSelected ? 'border-tg-button bg-tg-button/5 shadow-sm' : 'border-transparent bg-tg-secondary-bg hover:border-tg-hint/10'}`}
                          >
                            <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 flex items-center justify-center transition-all border-2 ${isMultiSelect ? 'rounded-md' : 'rounded-full'} ${isSelected ? 'bg-tg-button border-tg-button' : 'border-tg-hint/30'}`}>
                                    {isSelected && <div className={`bg-white ${isMultiSelect ? 'w-2 h-1 rotate-[-45deg] border-l-2 border-b-2 border-white bg-transparent mb-1 ml-0.5' : 'w-2 h-2 rounded-full'}`} />}
                                </div>
                                <span className={`font-medium text-sm ${isSelected ? 'text-tg-text' : 'text-tg-text/80'}`}>{option.name}</span>
                            </div>
                            {renderPrice()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ВОССТАНОВЛЕННЫЙ ПОДВАЛ (С КНОПКАМИ КОЛИЧЕСТВА) */}
            <div className="p-4 bg-tg-bg/95 backdrop-blur-xl absolute bottom-0 w-full z-10">
                <div className="flex gap-3">
                    <div className="flex items-center bg-tg-secondary-bg rounded-2xl px-2 h-14 border border-transparent">
                        <button onClick={() => { if(quantity>1) { setQuantity(q => q - 1); triggerHaptic('selection'); }}} className="p-3 active:opacity-50"><Minus size={20}/></button>
                        <span className="w-8 text-center font-bold text-lg">{quantity}</span>
                        <button onClick={() => { setQuantity(q => q + 1); triggerHaptic('selection'); }} className="p-3 active:opacity-50"><Plus size={20}/></button>
                    </div>

                    <button 
                        onClick={handleAddToCart} 
                        className="flex-1 bg-tg-button text-white h-14 rounded-2xl font-bold text-lg shadow-xl shadow-tg-button/20 flex justify-center items-center gap-2 active:scale-95 transition-all"
                    >
                        <ShoppingBag size={20} className="text-white" />
                        <span>{t.cart.add || "В корзину"}</span>
                        <span className="bg-white/20 px-3 py-1 rounded-lg text-sm font-bold">
                            {totalPrice.toFixed(0)} {t.currency}
                        </span>
                    </button>
                </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;
